import React from 'react';
import { useState, useMemo } from "react";
import { createCard, checkMqttCard, compressCardImage } from "../../utils/cardUtils";
import { publishMQTTStatus } from "../../utils/mqttUtils";
import { useActions, useUpdateCardMutation } from "../../utils/reactQueryHooks";
import Loading from "../common/Loading";
import ThresholdUpdateModal from "../common/ThresholdUpdateModal";
import PropTypes from 'prop-types';
import "../../styles/chartSettings.css";

export default function ChartSettings({ card, onSave, onResultModalClose }) {
    // Use React Query to fetch all actions
    const { data: allActions = [] } = useActions();
    
    // Use React Query mutation for updating cards
    const updateCardMutation = useUpdateCardMutation();
    
    // Filter actions for this specific sensor using useMemo
    const actionCards = useMemo(() => {
        return allActions.filter(action => action.sensor === card?.macAddress);
    }, [allActions, card?.macAddress]);

    // Track overall form loading state
    const [isFormLoading, setIsFormLoading] = useState(false);

    // Threshold update modal state
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateResult, setUpdateResult] = useState(null);

    // Image upload states
    const [cardImage, setCardImage] = useState(card?.cardImage || null);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [imageUploaded, setImageUploaded] = useState(!!card?.cardImage);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");

    // Ensure we convert any numeric values to strings for consistent form handling
    const normalizeValue = (value) => {
        if (value === null || value === undefined) return '';
        return value.toString();
    };
    
    // Initialize settings with normalized values from card
    const [settings, setSettings] = useState({
        macAddress: normalizeValue(card?.macAddress),
        addIrrigation: normalizeValue(card?.addIrrigation || 'none'),
        humidityHigh: normalizeValue(card?.humidityHigh),
        humidityLow: normalizeValue(card?.humidityLow),
        temperatureHigh: normalizeValue(card?.temperatureHigh),
        temperatureLow: normalizeValue(card?.temperatureLow),
        waterHigh: normalizeValue(card?.waterHigh),
        waterLow: normalizeValue(card?.waterLow),
        water2High: normalizeValue(card?.water2High),
        water2Low: normalizeValue(card?.water2Low),
        lightLow: normalizeValue(card?.lightLow),
        lightHigh: normalizeValue(card?.lightHigh),
    });

    //Handling input changes for all fields
    const handleInputChange = (e) => {
        //Extract key,value pair from target object
        const {name, value} = e.target;

        setSettings({
            ...settings,
            [name]: value
        });     
    }

    // Handle image upload
    const handleImageChange = async (event) => {
        const file = event.target.files[0];

        //if a file isn't selected, just return:
        if (!file) {
            return;
        }

        // Reset states
        setUploadError("");
        setIsUploading(true);
        setImageUploaded(false);
        setUploadedFileName("");

        //Always downscale/compress so we never store full-resolution photos.
        try {
            const processedFile = await compressCardImage(file);
            setImageRemoved(false);
            setImageUploaded(true);
            setUploadedFileName(file.name);

            //Read and set the image:
            const reader = new FileReader();
            reader.onloadend = () => {
                setCardImage(reader.result);
                setIsUploading(false);
            };
            reader.readAsDataURL(processedFile);
        }
        catch (ex) {
            console.error("Image processing error: ", ex);
            setUploadError('Error processing image. Please try again.');
            setIsUploading(false);
        }
    }

    const handleRemoveImage = () => {
        setCardImage(null);
        setImageRemoved(true);
        setImageUploaded(false);
        setUploadedFileName("");
        setUploadError("");
        // Reset the file input
        const fileInput = document.getElementById('chartSettingsImageUpload');
        if (fileInput) {
            fileInput.value = '';
        }
    }

    //Handle form submission:
    const handleSave = async (e) => {
        e.preventDefault();

        // Set overall form loading state
        setIsFormLoading(true);

        try {
            //Combine null coelescing operator with ternary operator.
            //The effect: if settings.attribute is undefined, try new attribute, else use ''.
            const resolvedCardImage = imageRemoved
                ? ''
                : (cardImage ?? card?.cardImage ?? '');

            let newCard = createCard(
                card?.macAddress ?? '',                 // Use card's MAC address directly.
                card?.sensorDescription ?? '', 
                resolvedCardImage, 
                card?.mostRecentBattery ?? '',
                card?.mostRecentTemp ?? '', 
                card?.mostRecentHumidity ?? '', 
                card?.mostRecentTimestamp ?? '',
                card?.mostRecentWater ?? '',
                settings?.addIrrigation ?? card?.addIrrigation ?? 'none',  //Preserve existing irrigation setting.
                settings?.humidityHigh ?? card?.humidityHigh ?? '',
                settings?.humidityLow ?? card?.humidityLow ?? '',
                settings?.temperatureHigh ?? card?.temperatureHigh ?? '',
                settings?.temperatureLow ?? card?.temperatureLow ?? '',
                settings?.waterHigh ?? card?.waterHigh ?? '',
                settings?.waterLow ?? card?.waterLow ?? '',
                card?.lightHigh ?? '',
                card?.lightLow ?? '',
                settings?.water2High ?? card?.water2High ?? '',
                settings?.water2Low ?? card?.water2Low ?? ''
            );

            // Check which specific thresholds have changed
            const changedThresholds = {
                humidity: {
                    changed: settings.humidityHigh !== normalizeValue(card?.humidityHigh) || 
                             settings.humidityLow !== normalizeValue(card?.humidityLow),
                    values: { low: settings.humidityLow, high: settings.humidityHigh }
                },
                temperature: {
                    changed: settings.temperatureHigh !== normalizeValue(card?.temperatureHigh) || 
                             settings.temperatureLow !== normalizeValue(card?.temperatureLow),
                    values: { low: settings.temperatureLow, high: settings.temperatureHigh }
                },
                water: {
                    changed: settings.waterHigh !== normalizeValue(card?.waterHigh) || 
                             settings.waterLow !== normalizeValue(card?.waterLow),
                    values: { low: settings.waterLow, high: settings.waterHigh }
                },
                water2: {
                    changed: settings.water2High !== normalizeValue(card?.water2High) || 
                             settings.water2Low !== normalizeValue(card?.water2Low),
                    values: { low: settings.water2Low, high: settings.water2High }
                }
            };

            // Check if any thresholds changed
            const anyThresholdsChanged = Object.values(changedThresholds).some(threshold => threshold.changed);
            const changedFields = Object.keys(changedThresholds).filter(key => changedThresholds[key].changed);

            let updateResult = null;

            if(checkMqttCard(card?.macAddress) && anyThresholdsChanged) {
                if (actionCards && actionCards.length > 0) {
                    
                    // Create complete threshold object (MQTT protocol requires all values)
                    // but we'll only send updates since something actually changed
                    const newThresholds = {
                        humidity: { 
                            low: settings.humidityLow || 'X', 
                            high: settings.humidityHigh || 'X' 
                        },
                        temperature: { 
                            low: settings.temperatureLow || 'X', 
                            high: settings.temperatureHigh || 'X' 
                        },
                        water: { 
                            low: settings.waterLow || 'X', 
                            high: settings.waterHigh || 'X' 
                        },
                        water2: { 
                            low: settings.water2Low || 'X', 
                            high: settings.water2High || 'X' 
                        }
                    };

                    // actionCards are already filtered to only include devices connected to this sensor
                    // (action.sensor === card.macAddress) - see useMemo on lines 20-23
                    
                    // Now filter by actuator type relevance to changed thresholds
                    const getRelevantActuatorTypes = (changedThresholds) => {
                        const relevant = [];
                        if (changedThresholds.humidity.changed) {
                            relevant.push('humidity', 'fan'); // Both humidity actuators and fans respond to humidity
                        }
                        if (changedThresholds.temperature.changed) {
                            relevant.push('temperature');
                        }
                        if (changedThresholds.water.changed || changedThresholds.water2.changed) {
                            relevant.push('irrigation');
                        }
                        return relevant;
                    };

                    const relevantActuatorTypes = getRelevantActuatorTypes(changedThresholds);
                    
                    // Collect skipped devices for modal display
                    const skippedDevices = [];
                    
                    const relevantActions = actionCards.filter(action => {
                        const isRelevant = relevantActuatorTypes.includes(action.actuatorType);
                        if (!isRelevant) {
                            skippedDevices.push({
                                name: action.actuatorDescription,
                                macAddress: action.macAddress,
                                actuatorType: action.actuatorType,
                                reason: `Not affected by ${changedFields.join(', ')} changes`
                            });
                            console.log(`  → Skipping ${action.actuatorDescription || action.macAddress} (${action.actuatorType}) - not affected by changed thresholds: ${changedFields.join(', ')}`);
                        }
                        return isRelevant;
                    });

                    
                    

                    


                                            // Check if we have any relevant devices to update
                        if (relevantActions.length === 0) {
                            
                            // Show modal with information about skipped devices and missing actuator types
                            updateResult = {
                                success: true,
                                changedThresholds: changedFields,
                                sensorMac: card.macAddress,
                                totalDevices: actionCards.length,
                                relevantDevices: 0,
                                successfulDevices: [],
                                failedDevices: [],
                                skippedDevices: skippedDevices,
                                relevantActuatorTypes: relevantActuatorTypes,
                                missingActuatorTypes: relevantActuatorTypes // All relevant types are missing
                            };
                            setUpdateResult(updateResult);
                            
                            // Clear loading immediately since no MQTT operations are needed
                            setIsFormLoading(false);
                            setShowUpdateModal(true);
                            

                            // Continue to database save logic instead of returning early
                        }

                    // MQTT Timeout: Each publishMQTTStatus call has a built-in 4-second timeout
                    // If a device is offline/disconnected, the call will fail with "failed: timeout"
                    const updatePromises = relevantActions.map(action => {
                        // For irrigation devices, include duration parameter
                        if (action.actuatorType === 'irrigation') {
                            return publishMQTTStatus(
                                action.macAddress,      // The MAC address of the actuator device
                                card.macAddress,        // The MAC address of the sensor card providing data/thresholds
                                newThresholds,          // Complete threshold settings (MQTT protocol requirement)
                                action.tracking,        // The tracking mode for this specific action device
                                action.duration   // Duration for irrigation devices (default 4 seconds)
                            );
                        } else {
                            return publishMQTTStatus(
                                action.macAddress,      // The MAC address of the actuator device
                                card.macAddress,        // The MAC address of the sensor card providing data/thresholds
                                newThresholds,          // Complete threshold settings (MQTT protocol requirement)
                                action.tracking         // The tracking mode for this specific action device
                            );
                        }
                    });

                    try {
                        const results = await Promise.all(updatePromises);
                        
                        const successfulDevices = [];
                        const failedDevices = [];
                        
                        relevantActions.forEach((action, index) => {
                            const result = results[index];
                            const success = result === "success";
                            const deviceInfo = {
                                name: action.actuatorDescription,
                                macAddress: action.macAddress,
                                actuatorType: action.actuatorType,
                                reason: success ? null : result
                            };
                            
                            if (success) {
                                successfulDevices.push(deviceInfo);
                            } else {
                                failedDevices.push(deviceInfo);
                                console.error(`Failed to update ${deviceInfo.name || deviceInfo.macAddress} (sensor: ${card.macAddress}): ${result}`);
                            }
                        });

                        const overallSuccess = failedDevices.length === 0;
                        
                        // Store result for database save decision and show detailed modal
                        updateResult = {
                            success: overallSuccess,
                            changedThresholds: changedFields,
                            sensorMac: card.macAddress,
                            totalDevices: actionCards.length,
                            relevantDevices: relevantActions.length,
                            successfulDevices: successfulDevices,
                            failedDevices: failedDevices,
                            skippedDevices: skippedDevices,
                            relevantActuatorTypes: relevantActuatorTypes
                        };
                        setUpdateResult(updateResult);
                        
                        // Clear loading immediately since MQTT operations are complete
                        setIsFormLoading(false);
                        setShowUpdateModal(true);
                        // Continue to database save logic instead of returning early

                    } catch (error) {
                        console.error("Error publishing MQTT threshold updates:", error);
                        
                        // Store error result and show error modal
                        updateResult = {
                            success: false,
                            changedThresholds: changedFields,
                            sensorMac: card.macAddress,
                            totalDevices: actionCards.length,
                            relevantDevices: relevantActions.length,
                            successfulDevices: [],
                            failedDevices: relevantActions.map(action => ({
                                name: action.actuatorDescription,
                                macAddress: action.macAddress,
                                actuatorType: action.actuatorType,
                                reason: "Connection error"
                            })),
                            skippedDevices: skippedDevices,
                            relevantActuatorTypes: relevantActuatorTypes
                        };
                        setUpdateResult(updateResult);
                        
                        // Clear loading immediately since MQTT operations failed
                        setIsFormLoading(false);
                        setShowUpdateModal(true);
                        

                        return; // Don't continue to database save logic - MQTT failed
                    }
                } else {
                    
                    // Still show user feedback that thresholds were changed, even though no devices need updates
                    updateResult = {
                        success: true,
                        changedThresholds: changedFields,
                        sensorMac: card.macAddress,
                        totalDevices: 0,
                        relevantDevices: 0,
                        successfulDevices: [],
                        failedDevices: [],
                        skippedDevices: [],
                        relevantActuatorTypes: []
                    };
                    setUpdateResult(updateResult);
                    
                    // Clear loading immediately since no devices are connected
                    setIsFormLoading(false);
                    setShowUpdateModal(true);
                    
                    
                    // Continue to database save logic instead of returning early
                }
            }

            // Step 2: Only save to database if MQTT updates were successful (or not needed)
            // If MQTT was needed but failed, we DON'T save the changes to maintain data consistency
            let databaseUpdateSuccessful = false;
            
            if (anyThresholdsChanged && checkMqttCard(card?.macAddress)) {
                // MQTT updates were attempted - check if they were successful
                if (updateResult && updateResult.success && updateResult.successfulDevices.length > 0) {
    
                    try {
                        await updateCardMutation.mutateAsync(newCard);
                        databaseUpdateSuccessful = true;

                    } catch (error) {
                        console.error('Error in updateCardMutation.mutateAsync:', error);
                        databaseUpdateSuccessful = false;
                    }
                } else if (updateResult && !updateResult.success) {
                    
                    setIsFormLoading(false);
                    // Modal will show the failure details, so we return early without saving
                    return;
                } else if (updateResult && updateResult.successfulDevices.length === 0) {
                    
                    setIsFormLoading(false);
                    // Modal will show the details, so we return early without saving
                    return;
                }
            } else {
                // No MQTT updates needed (description-only change or no connected devices)

                try {
                    await updateCardMutation.mutateAsync(newCard);
                    databaseUpdateSuccessful = true;
                    
                } catch (error) {
                                            console.error('Error in updateCardMutation.mutateAsync:', error);
                    databaseUpdateSuccessful = false;
                }
            }

            // Clear loading state
            setIsFormLoading(false);

            // Only call onSave if the database update was successful
            
            if (databaseUpdateSuccessful) {
                onSave(newCard);
            } else {
                // Database update failed, but MQTT operations may have succeeded
                // User will see results in the modal
            }
        } catch (error) {
            console.error("Error updating card:", error);
            setIsFormLoading(false);
            alert(`Error updating card: ${error.message}`);
        }
    }; // Closes handleSave function

    return (
        <>
            {isFormLoading && <Loading zIndex={900} />}
            <ThresholdUpdateModal 
                isOpen={showUpdateModal}
                onClose={() => {
                    setShowUpdateModal(false);
                    onResultModalClose?.();
                }}
                updateResult={updateResult}
            />
            <div className="chart-settings-container">
                <h3 className="chart-settings-title">Chart Settings</h3>
                <form className="chart-settings-form" onSubmit={handleSave}>
                
                {/* Metrics Section */}
                <div className="metrics-section">
                    <div className="metrics-header">
                        <span className="metric-label-header" aria-hidden="true" />
                        <span className="column-header">Low</span>
                        <span className="column-header">High</span>
                    </div>
                    <div className="metric-row">
                        <label className="metric-label" htmlFor="humidityLow">
                            Humidity
                            <i className="ri-water-percent-line animated-green" aria-hidden="true" />
                        </label>
                        <div className="metric-inputs">
                            <input
                                type="number"
                                id="humidityLow"
                                name="humidityLow"
                                value={settings.humidityLow}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="100"
                                placeholder="0%"
                                aria-label="Humidity low"
                            />
                            <input
                                type="number"
                                name="humidityHigh"
                                value={settings.humidityHigh}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="100"
                                placeholder="100%"
                                aria-label="Humidity high"
                            />
                        </div>
                    </div>

                    <div className="metric-row">
                        <label className="metric-label" htmlFor="temperatureLow">
                            Temperature
                            <i className="ri-temp-cold-line animated-red" aria-hidden="true" />
                        </label>
                        <div className="metric-inputs">
                            <input
                                type="number"
                                id="temperatureLow"
                                name="temperatureLow"
                                value={settings.temperatureLow}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="120"
                                placeholder="0°F"
                                aria-label="Temperature low"
                            />
                            <input
                                type="number"
                                name="temperatureHigh"
                                value={settings.temperatureHigh}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="120"
                                placeholder="120°F"
                                aria-label="Temperature high"
                            />
                        </div>
                    </div>

                    <div className="metric-row">
                        <label className="metric-label" htmlFor="waterLow">
                            Water Level
                            <i className="ri-contrast-drop-2-line animated-blue" aria-hidden="true" />
                        </label>
                        <div className="metric-inputs">
                            <input
                                type="number"
                                id="waterLow"
                                name="waterLow"
                                value={settings.waterLow}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="100"
                                placeholder="0 in"
                                aria-label="Water level low"
                            />
                            <input
                                type="number"
                                name="waterHigh"
                                value={settings.waterHigh}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="100"
                                placeholder="100 in"
                                aria-label="Water level high"
                            />
                        </div>
                    </div>

                    <div className="metric-row">
                        <label className="metric-label" htmlFor="water2Low">
                            Water Level 2
                            <i className="ri-contrast-drop-2-fill animated-blue" aria-hidden="true" />
                        </label>
                        <div className="metric-inputs">
                            <input
                                type="number"
                                id="water2Low"
                                name="water2Low"
                                value={settings.water2Low}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="100"
                                placeholder="0 in"
                                aria-label="Water level 2 low"
                            />
                            <input
                                type="number"
                                name="water2High"
                                value={settings.water2High}
                                onChange={handleInputChange}
                                className="threshold-input"
                                min="0"
                                max="100"
                                placeholder="100 in"
                                aria-label="Water level 2 high"
                            />
                        </div>
                    </div>
                </div>

                {/* Image Upload Section */}
                <div className="image-section">
                    <label htmlFor="chartSettingsImageUpload" className="image-label">
                            Card Image
                        <i className="ri-image-line"></i>
                        </label>
                        <input
                            type="file"
                            id="chartSettingsImageUpload"
                            accept="image/*"
                            onChange={handleImageChange}
                            style={{ display: 'none' }}
                        />
                        
                    <div className="image-buttons">
                            <label 
                                htmlFor="chartSettingsImageUpload" 
                                className={`upload-button ${isUploading ? 'uploading' : imageUploaded ? 'uploaded' : 'default'}`}
                            >
                                {isUploading ? (
                                    <>
                                    <i className="ri-loader-4-line loading"></i>
                                        <span>Processing...</span>
                                    </>
                                ) : imageUploaded ? (
                                    <>
                                    <i className="ri-check-line"></i>
                                        <span>Change Image</span>
                                    </>
                                ) : (
                                    <>
                                    <i className="ri-upload-line"></i>
                                        <span>Choose File</span>
                                    </>
                                )}
                            </label>
                            
                            {imageUploaded && (
                                <button 
                                    type="button" 
                                    onClick={handleRemoveImage}
                                    title="Remove image"
                                    className="remove-button"
                                >
                                <i className="ri-delete-bin-line"></i>
                                </button>
                            )}
                        </div>
                        
                        {uploadedFileName && (
                            <div className="file-info">
                            <i className="ri-image-line"></i>
                            <span>{uploadedFileName}</span>
                            </div>
                        )}
                        
                        {uploadError && (
                            <div className="upload-error">
                            <i className="ri-error-warning-line"></i>
                                <span>{uploadError}</span>
                            </div>
                        )}
                </div>

                {/* Save Button */}
                <div className="save-section">
                    <button 
                        className="green-button save-button" 
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </div>
            </form>
        </div>
        </>
    );
}

// Prop validation
ChartSettings.propTypes = {
    card: PropTypes.shape({
        macAddress: PropTypes.string,
        sensorDescription: PropTypes.string,
        addIrrigation: PropTypes.string,
        humidityHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        humidityLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        temperatureHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        temperatureLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        waterHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        waterLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        water2High: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        water2Low: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        cardImage: PropTypes.oneOfType([PropTypes.string]),
        mostRecentBattery: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mostRecentTemp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), 
        mostRecentHumidity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), 
        mostRecentTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mostRecentWater: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lightLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lightHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onSave: PropTypes.func.isRequired,
    onResultModalClose: PropTypes.func,
};